import * as React from 'react';

import {
  enumPaymentMethod,
  PaymentMethod,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DatePicker,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  formatMoney,
  TextArea,
  TextField,
  type DateValue,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { netPayAtom } from '../../_graphql/mutationNetPay';
import { nextCheckNumberQueryAtom } from '../../_graphql/queryNextCheckNumber';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectFinancialAccount } from '~/shared/components/select-fields/SelectFinancialAccount';
import { now } from '~/shared/utils/dateTime';

interface NetPayDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
  onSuccess?: () => void;
}

export function NetPayDialog({
  isOpen,
  onClose,
  accountId,
  accountName,
  onSuccess,
}: NetPayDialogProps) {
  const t = useTranslations();
  const toast = useToast();
  const netPay = useSetAtom(netPayAtom());

  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>(
    enumPaymentMethod.ACH,
  );
  const [transactionDate, setTransactionDate] =
    React.useState<DateValue | null>(now());
  const [financialAccountId, setFinancialAccountId] = React.useState<
    string | null
  >(null);
  const [checkFacilityId, setCheckFacilityId] = React.useState<string | null>(
    null,
  );
  const [checkNumber, setCheckNumber] = React.useState<string>('');
  const [checkMemo, setCheckMemo] = React.useState<string>('');
  const [achNumber, setAchNumber] = React.useState<string | null>(null);
  const [wireNumber, setWireNumber] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const isCheckPaymentForQuery =
    paymentMethod === 'Check' && !!financialAccountId;
  const $nextCheckNumber = React.useMemo(
    () =>
      nextCheckNumberQueryAtom(() => ({
        variables: { financialAccountId: financialAccountId ?? '' },
        skip: !isCheckPaymentForQuery,
      })),
    [financialAccountId, isCheckPaymentForQuery],
  );
  const { data: nextCheckNumber } = useAtomValue($nextCheckNumber);

  React.useEffect(() => {
    if (isCheckPaymentForQuery && nextCheckNumber) {
      setCheckNumber(nextCheckNumber);
    }
  }, [isCheckPaymentForQuery, nextCheckNumber]);

  const resetForm = React.useCallback(() => {
    setPaymentMethod(enumPaymentMethod.ACH);
    setTransactionDate(now());
    setFinancialAccountId(null);
    setCheckFacilityId(null);
    setCheckNumber('');
    setCheckMemo('');
    setAchNumber(null);
    setWireNumber(null);
  }, []);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!transactionDate || !financialAccountId) return;

    setIsSubmitting(true);
    try {
      const result = await netPay({
        accountId,
        paymentMethod,
        transactionDate,
        financialAccountId,
        checkFacilityId: checkFacilityId || undefined,
        checkNumber: checkNumber || undefined,
        checkMemo: checkMemo || undefined,
        achNumber: achNumber || undefined,
        wireNumber: wireNumber || undefined,
      });

      const { payableTotal, receivableTotal, netAmount } = result.financeNetPay;

      toast.success(
        `${t.balances.netPay.messages.success} - ` +
          `${t.balances.netPay.result.payableTotal}: ${formatMoney(payableTotal.amount, payableTotal.currencyCode)}, ` +
          `${t.balances.netPay.result.receivableTotal}: ${formatMoney(receivableTotal.amount, receivableTotal.currencyCode)}, ` +
          `${t.balances.netPay.result.netAmount}: ${formatMoney(netAmount.amount, netAmount.currencyCode)}`,
      );

      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.messages.common.unknownError,
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    transactionDate,
    accountId,
    paymentMethod,
    financialAccountId,
    checkFacilityId,
    checkNumber,
    checkMemo,
    achNumber,
    wireNumber,
    netPay,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  if (!isOpen) return null;

  const isCheckPayment = paymentMethod === 'Check';
  const canSubmit = transactionDate != null && financialAccountId != null;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onClose}
      className="@NetPayDialog"
    >
      <DialogContent position="center" isKeyboardDismissDisabled>
        <DialogHeader slot="title">
          <DialogTitle>{t.balances.netPay.title}</DialogTitle>
        </DialogHeader>

        <DialogMain>
          <div className="flex flex-col gap-4">
            <div>
              <span className="text-sm font-semibold text-neutral">
                {t.balances.netPay.fields.accountName}
              </span>
              <p className="text-base">{accountName}</p>
            </div>

            <SelectFinancialAccount
              label={t.balances.netPay.fields.financialAccount}
              value={financialAccountId ?? undefined}
              onChange={(value) => setFinancialAccountId(value ?? null)}
              visibleInPayments={true}
              isRequired
            />

            <SelectEnum<PaymentMethod>
              label={t.balances.netPay.fields.paymentMethod}
              enumValue={enumPaymentMethod}
              value={paymentMethod}
              onChange={(value) => value && setPaymentMethod(value)}
              isRequired
            />

            <DatePicker
              label={t.balances.netPay.fields.transactionDate}
              value={transactionDate as DateValue | undefined}
              onChange={setTransactionDate}
              isRequired
            />

            {isCheckPayment && (
              <>
                <SelectFacility
                  label={t.balances.netPay.fields.checkFacility}
                  value={checkFacilityId ?? undefined}
                  onChange={(value) => setCheckFacilityId(value ?? null)}
                />
                <TextField
                  label={t.balances.netPay.fields.checkNumber}
                  value={checkNumber}
                  onChange={setCheckNumber}
                />
                <TextArea
                  label={t.balances.netPay.fields.checkMemo}
                  value={checkMemo}
                  onChange={setCheckMemo}
                  rows={2}
                />
              </>
            )}
            {paymentMethod === 'ACH' && (
              <TextField
                label={t.balances.netPay.fields.achNumber}
                value={achNumber ?? ''}
                onChange={(value) => setAchNumber(value || null)}
              />
            )}
            {paymentMethod === 'Wire' && (
              <TextField
                label={t.balances.netPay.fields.wireNumber}
                value={wireNumber ?? ''}
                onChange={(value) => setWireNumber(value || null)}
              />
            )}
          </div>
        </DialogMain>

        <DialogFooter>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              {t.balances.netPay.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isPending={isSubmitting}
              isDisabled={!canSubmit}
            >
              {t.balances.netPay.buttons.submit}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
}
